<template>
    <div>
        <b-loading :is-full-page="true" :active="isLoading || isSaving"></b-loading>
        <div>
            <div class="level is-vcentered">
                <div class="level-left">
                    <h1 class="title is-size-4">
                        {{ isEdit ? 'Edit Project' : 'Create Project' }}
                        <span class="tag" :class="projectServiceHelper.getStateStyle(project.state)">{{ project.state }}</span>
                    </h1>
                </div>
                <div class="level-right">
                    <div class="field is-grouped is-grouped-right">
                        <p class="control">
                            <router-link tag="button" class="button is-outlined" to="/" :disabled="isSaving">Back</router-link>
                        <p>
                        <p class="control" v-if="isEdit">
                            <button class="button is-outlined is-primary" @click="openProjectCopyModal">Copy</button>
                        </p>
                        <p class="control" v-if="isPublished">
                            <button class="button is-outlined is-primary" @click="openProjectShareModal">Share</button>
                        </p>
                        <p class="control">
                            <button class="button is-outlined is-primary" @click="openProjectTransferModal" :disabled="isSaving || !isEdit">Transfer</button>
                        </p>
                        <p class="control" v-if="isPublished">
                            <button class="button is-outlined is-danger" @click="showConfirmArchiveModal(project)" :disabled="isSaving">Archive</button>
                        </p>
                        <p class="control" v-if="isDraft">
                            <button class="button is-outlined is-danger" @click="showConfirmDeleteModal(project)" :disabled="isSaving">Delete Draft</button>
                        </p>
                        <p class="control" v-if="isDraft">
                            <button class="button is-outlined is-success" @click="saveDraft" :disabled="isSaving">Save Draft</button>
                        </p>
                        <p class="control" v-if="isPublished || isArchived">
                            <button :class="'button is-outlined ' + projectServiceHelper.getStateStyle(project.state)"
                            @click="needRepublish ? openPublishConfirmationModal(projectColumns, savePublishedOrArchived, false) : savePublishedOrArchived()" :disabled="isSaving">
                                {{needRepublish ? 'Republish' : 'Save'}}
                            </button>
                        </p>
                        <p class="control" v-if="isDraft">
                            <button class="button is-outlined is-info" @click="openPublishConfirmationModal(projectColumns, publish, true)" :disabled="isSaving || !isEdit">Publish</button>
                        </p>
                        <p class="control" v-if="isArchived">
                            <button class="button is-outlined is-info" @click="publish" :disabled="isSaving || !isEdit">Republish</button>
                        </p>
                    </div>
                </div>
            </div>
            <b-notification type="is-danger" v-if="errorMessage" :closable="false">
                <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
            </b-notification>
            <section class="section project-details">
                <h2 class="title is-size-4">Project Details</h2>
                    <b-field label="Project Name" horizontal>
                        <b-input type="text" v-model="project.name" class="project-name-input" required></b-input>
                    </b-field>
                    <b-field label="Conversation Topic" horizontal :class="isDraft ? '' : 'is-baseline-aligned'">
                        <div class="modal-button-control">
                            <span class="control" :class="conversationTopic.name ? '' : 'is-italic'">{{ conversationTopic.name || 'No Conversation Topic selected'}}</span>
                            <button type="button" class="button" @click="openConversationTopicModal" v-if="isDraft">Select Conversation Topic</button>
                            <div v-if="conversationTopic.isAvailable != undefined">
                                <b-icon v-if="conversationTopic.isAvailable" type="is-success" title="Available" pack="fas" size="is-small" icon="check"></b-icon>
                                <b-icon v-if="!conversationTopic.isAvailable" type="is-danger" title="Unavailable" pack="fas" size="is-small" icon="times"></b-icon>
                            </div>
                        </div>
                    </b-field>
                    <b-field horizontal :class="isDraft ? '' : 'is-baseline-aligned'">
                        <template slot="label">
                            Conversation Theme <span class="is-italic" style="font-variant:all-petite-caps">Optional</span>
                        </template>
                        <div class="modal-button-control">
                            <span class="control" :class="conversationTheme.name ? '' : 'is-italic'">{{ conversationTheme.name || 'No Conversation Theme selected'}}</span>
                            <button type="button" class="button" @click="openConversationThemeModal" v-if="isDraft">Select Conversation Theme</button>
                            <button type="button" class="button is-outlined" style="margin-left: 10px" @click="clearConversationTheme" v-if="isDraft">Clear Theme</button>
                        </div>
                    </b-field>
                    <b-field label="Description" horizontal>
                        <b-input type="textarea" v-model="projectDescription" class="project-name-input"></b-input>
                    </b-field>
                    <!-- TODO: Metric Weights CRUD -->
                    <b-field label="Metric Weights" horizontal>
                        <div class="modal-button-control">
                            <span class="control is-italic">{{hasDefaultWeights ? 'Default Weights' : 'Custom Weights'}}</span>
                            <button type="button" class="button" @click="openWeightAdjustmentModal()">Adjust Metric Weights</button>
                        </div>
                    </b-field>
                    <b-field label="Project End Date" horizontal>
                        <b-datepicker placeholder="Select a date..."
                                      :min-date="minProjectEndDate"
                                      v-model="projectEndDate"
                                      icon="fa fa-calendar">
                        </b-datepicker>
                    </b-field>
                    <b-field label="Share With Everyone" horizontal v-if="isPublished" class="is-center-aligned">
                        <b-checkbox size="is-medium" v-model="project.isSharedWithEveryone" @change.native="confirmShareUnshareWithEveryone">
                            <p class="info-text">
                                Sharing this project with everyone will make this project available to all the users except client-users.
                            </p>
                            <p class="info-text">
                                Unsharing this project will make this project unavailable to all users except the project owner
                                and any users that it's been specifically shared with.
                            </p>
                        </b-checkbox>
                    </b-field>
            </section>
        </div>
        <div class=" box">
            <div class="level is-vcentered">
                <div class="level-left">
                    <h2 class="title is-size-4">Project Brand Columns</h2>
                </div>
                <div class="level-right" v-if="isPublished || isDraft">
                    <button type="button" class="button is-primary is-pulled-right" @click="openBrandColumnModal(false)">Add Brand Column </button>
                </div>
            </div>
            <b-table
                :data="projectColumns"
                :columns="projectColumnHeaders"
                :default-sort-direction="'asc'"
                :striped="true"
                default-sort="id"
                detailed
                :show-detail-icon="true"
            >
                <template slot-scope="props">
                    <b-table-column field="name" label="Name" sortable>
                        <b-input type="text"
                                 v-model="props.row.name"
                        ></b-input>
                    </b-table-column>
                    <b-table-column field="colour" label="" width ="10">
                        <verte v-model="props.row.colour" menuPosition="center" :enableAlpha="false" model="hex"></verte>
                    </b-table-column>
                    <b-table-column field="brandTopic" label="Netbase Brand Topic" centered sortable >
                        <span>{{ props.row.brandTopic.name }}</span>
                    </b-table-column>
                    <b-table-column field="brandTopic" label="Topic status" centered sortable >
                        <div v-if="props.row.brandTopic.isAvailable != undefined">
                            <b-icon v-if="props.row.brandTopic.isAvailable" type="is-success" pack="fas" size="is-small" icon="check"></b-icon>
                            <b-icon v-if="!props.row.brandTopic.isAvailable" type="is-danger" pack="fas" size="is-small" icon="times"></b-icon>
                        {{ props.row.brandTopic.isAvailable ? 'Available' : 'Unavailable' }}
                        </div>
                    </b-table-column>
                    <b-table-column field="brandTheme" label="NB Brand Theme" centered sortable >
                        <span>{{ props.row.brandTheme && props.row.brandTheme.name ? props.row.brandTheme.name : 'None' }}</span>
                    </b-table-column>
                    <b-table-column field="fbPageId" label="FB Page" sortable >
                        <span v-if="props.row.fbPageId">
                            <i v-if="isSocialtoolsPageNotActive(props.row.fbPageInfo)" class="fas fa-times is-small has-text-danger" :title="props.row.fbPageInfo.activeStatus"></i>
                            {{ isSocialtoolsPageHasName(props.row.fbPageInfo) ? props.row.fbPageInfo.name : props.row.fbPageId }}
                        </span>
                        <span v-else>N/A</span>
                    </b-table-column>
                    <b-table-column field="twPageId" label="TW Page" sortable >
                        <span v-if="props.row.twPageId">
                            <i v-if="isSocialtoolsPageNotActive(props.row.twPageInfo)" class="fas fa-times is-small has-text-danger" :title="props.row.twPageInfo.activeStatus"></i>
                            {{ isSocialtoolsPageHasName(props.row.twPageInfo) ? props.row.twPageInfo.name : props.row.twPageId }}
                        </span>
                        <span v-else>N/A</span>
                    </b-table-column>
                    <b-table-column field="ytPageId" label="YT Page" sortable >
                        <span v-if="props.row.ytPageId">
                            <i v-if="isSocialtoolsPageNotActive(props.row.ytPageInfo)" class="fas fa-times is-small has-text-danger" :title="props.row.ytPageInfo.activeStatus"></i>
                            {{ isSocialtoolsPageHasName(props.row.ytPageInfo) ? props.row.ytPageInfo.name : props.row.ytPageId }}
                        </span>
                        <span v-else>N/A</span>
                    </b-table-column>
                    <b-table-column field="igPageId" label="IG Page" sortable >
                        <span v-if="props.row.igPageId">
                            <i v-if="isSocialtoolsPageNotActive(props.row.igPageInfo)" class="fas fa-times is-small has-text-danger" :title="props.row.igPageInfo.activeStatus"></i>
                            {{ isSocialtoolsPageHasName(props.row.igPageInfo) ? props.row.igPageInfo.name : props.row.igPageId }}
                        </span>
                        <span v-else>N/A</span>
                    </b-table-column>
                    <b-table-column class="commands" label="Commands" width="100" :visible="isDraft || isPublished || hasNewColumns">
                        <button class="level-item button is-outlined is-pulled-right" @click="deleteColumn(props.row)">
                            <b-icon icon="trash-alt" type="is-danger"></b-icon>
                        </button>
                        <button class="level-item button is-outlined is-pulled-right" @click="openBrandColumnModal(props.row, isPublished)">
                            <b-icon icon="edit" type="is-primary"></b-icon>
                        </button>
                    </b-table-column>
                </template>
                <template slot="detail" slot-scope="props">
                    <div>
                        <label class="has-text-weight-semibold">Keywords: </label>
                        {{ props.row.keywords.join(', ') }}
                    </div>
                    <div>
                        <label class="has-text-weight-semibold">Excluded Keywords: </label>
                        {{ props.row.excludedKeywords.join(', ') }}
                    </div>
                    <socialtools-pages-info :projectColumn="props.row" />
                </template>
                <!-- Empty Table Template -->
                <template slot="empty">
                    <section class="section">
                        <div class="content has-text-grey has-text-centered" @click="openBrandColumnModal(false)">
                            <p>
                                <b-icon
                                    icon="list"
                                    size="is-large">
                                </b-icon>
                            </p>
                            <p>Click to add a Brand Column </p>
                        </div>
                    </section>
                </template>
            </b-table>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import nanoidGenerate from 'nanoid/generate';

import projectService from '@/services/project-service.js';
import netbaseService from '@/services/netbase-service.js';
import defaultMetricWeightService from '@/services/default-metric-weight-service';
import colourService from '@/services/colour-service.js';
import { projectServiceHelper } from '@/services/project-service-helper.js';
import { dateHelperService } from '@/services/date-helper-service';
import utilities from '@/services/utility-service.js';

import constants from '@/constants.js';

import BrandColumnForm from '@/components/BrandColumnForm.vue';
import NetBaseTopicSelection from '@/components/NetBaseTopicSelection.vue';
import NetBaseThemeSelection from '@/components/NetBaseThemeSelection.vue';
import ProjectWeightAdjustment from '@/components/ProjectWeightAdjustment.vue';
import ProjectShare from '@/components/ProjectShare.vue';
import ProjectTransferForm from '@/components/ProjectTransferForm.vue';
import ProjectCopyForm from '@/components/ProjectCopyForm.vue';
import SocialtoolsPagesInfo from '@/components/SocialtoolsPagesInfo.vue';
import ProjectPublishConfirmation from '../components/ProjectPublishConfirmation.vue';

export default {
    name: 'CreateProject',
    components: {
        SocialtoolsPagesInfo
    },
    props: {
        projectId: {
            type: Number,
            required: false
        }
    },
    data () {
        return {
            projectServiceHelper,
            utilities,
            isEdit: false,
            errorMessage: '',
            isPublish: false,
            isLoading: true,
            isSaving: false,
            needRepublish: false,
            conversationTopic: {},
            conversationTheme: {},
            projectColumns: [],
            projectDescription: '',
            minProjectEndDate: new Date(moment().subtract(1, 'days')), // This aspect of the minProjectEndDate is only used when creating a new project, therefore we are happy to use the user's local datetime format.
            projectEndDate: new Date(moment().add(1, 'years')),
            project: {
                name: '',
                state: constants.PROJECT_STATE.DRAFT
            },
            // Used to show the project column table headers even when table is empty: https://github.com/buefy/buefy/issues/568
            placeholderColumnHeaders: [
                { label: 'Name' },
                { label: 'Netbase Brand Topic' },
                { label: 'FB Page' },
                { label: 'TW Page' },
                { label: 'YT Page' },
                { label: 'IG Page' }
            ],

            projectMetricWeights: [],
            projectPillarWeights: [],
            defaultMetricWeights: [],
            defaultPillarWeights: [],
            originalContent: {},
            forceRedirect: false
        };
    },
    async created () {
        let userId = localStorage.getItem('userId');
        let isNetBaseAuthenticated = localStorage.getItem('isNetBaseAuthenticated');
        if (isNetBaseAuthenticated === 'true') {
            // call these endpoints to cache the topics and themes in service module for future use
            netbaseService.getTopics(userId);
            netbaseService.getThemes(userId);
        } else {
            this.errorMessage = 'NetBase has not been authenticated';
        }

        try {
            // If the projectId is present then we are in edit mode, otherwise we are in create mode.
            if (this.projectId) {
                // Check whether the projectId is an int, if not redirect the user to project list view.
                if (parseInt(this.projectId, 10)) {
                    this.isEdit = true;

                    let response = await projectService.getProject(this.projectId);
                    let projectToEdit = response.data;
                    this.project = projectToEdit;
                    this.projectEndDate = new Date(this.project.endDate);

                    // Redirect if the user is not the project owner
                    if (!this.isProjectOwner(this.project)) {
                        this.forceRedirectToProjectListView();
                    }

                    // If the project is published or archived then set the project end minumum date to the project start date,
                    // otherwise set the minimum date to the project created date.
                    if (this.project.startDate) {
                        this.minProjectEndDate = new Date(moment(this.project.startDate).subtract(1, 'days'));
                    } else {
                        this.minProjectEndDate = new Date(moment(this.project.created).subtract(1, 'days'));
                    }

                    let projectJSON = JSON.parse(projectToEdit.projectJSON);
                    setColoursToColumns(projectJSON.columns); // want to do this before setting the columns on the observed object

                    this.projectDescription = projectJSON.description || '';
                    this.projectColumns = projectJSON.columns || [];
                    this.conversationTopic = projectJSON.conversationTopic ? { topicId: projectJSON.conversationTopic.id, name: projectJSON.conversationTopic.name, isAvailable: projectJSON.conversationTopic.isAvailable } : {};
                    this.conversationTheme = projectJSON.conversationTheme ? { themeId: projectJSON.conversationTheme.id, name: projectJSON.conversationTheme.name } : {};
                    this.projectMetricWeights = projectJSON.metricWeights || [];
                    this.projectPillarWeights = projectJSON.pillarWeights || [];
                } else {
                    // ProjectId is not an int, so redirect the user to project list view.
                    this.isLoading = false;
                    this.forceRedirectToProjectListView();
                }
            } else {
                this.isLoading = false;
            }

            this.defaultMetricWeights = (await defaultMetricWeightService.getDefaultMetricWeights()).data;
            this.defaultPillarWeights = (await defaultMetricWeightService.getDefaultPillarWeights()).data;

            this.originalContent = this.serializedContent();
        } catch (error) {
            this.errorMessage = error.message;
        } finally {
            this.isLoading = false;
        }

        // Subscribe to windows beforeunload to check for unsaved changes
        // Use a method defined below to ensure correct scope is accessable
        window.addEventListener('beforeunload', this.warnOnWindowChange);
    },
    methods: {
        async saveDraft () {
            await handleProjectChangeWith(this, this.project.name + ' has been saved', () => {
                this.project.state = this.project.state || constants.PROJECT_STATE.DRAFT;
                this.project.projectJSON = this.buildProjectJSON();
                // buefy datepicker only returns local date/time (time is fixed at 00:00:00). As we want users to select UTC date (and time doesn't matter),
                // we will only keep the date value and convert it to a UTC datetime so with the same date value
                this.project.endDate = dateHelperService.convertToUTC(this.projectEndDate);
                return this.isEdit
                    ? projectService.updateDraftProject(this.project)
                    : projectService.createProject(this.project);
            });
        },
        async savePublishedOrArchived () {
            if (this.needRepublish) {
                await handleProjectChangeWith(this, this.project.name + ' has been republished', async () => {
                    this.needRepublish = false;
                    this.project.projectJSON = this.buildProjectJSON();
                    this.project.endDate = dateHelperService.convertToUTC(this.projectEndDate);
                    // We need to check state here, as republishing may be due to a column change
                    // or an ARCHIVED -> PUBLISHED state change. Validation is different in each case.
                    if (this.project.state === constants.PROJECT_STATE.ARCHIVED) {
                        await projectService.updateArchivedProject(this.project);
                    } else {
                        await projectService.updatePublishedProject(this.project);
                    }
                    return projectService.republishProject(this.projectId);
                });
            } else {
                await handleProjectChangeWith(this, this.project.name + ' has been saved', () => {
                    this.project.projectJSON = this.buildProjectJSON();
                    // buefy datepicker only returns local date/time (time is fixed at 00:00:00). As we want users to select UTC date (and time doesn't matter),
                    // we will only keep the date value and convert it to a UTC datetime so with the same date value
                    this.project.endDate = dateHelperService.convertToUTC(this.projectEndDate);
                    if (this.project.state === constants.PROJECT_STATE.PUBLISHED) {
                        return projectService.updatePublishedProject(this.project);
                    }
                    return projectService.updateArchivedProject(this.project);
                });
            }
        },
        async publish () {
            // We need to check state here, as republishing validation is different to publishing.
            if (this.project.state === constants.PROJECT_STATE.ARCHIVED) {
                await handleProjectChangeWith(this, this.project.name + ' has been republished', () => {
                    return projectService.republishProject(this.projectId);
                });
            } else {
                await handleProjectChangeWith(this, this.project.name + ' has been published', () => {
                    return projectService.publishProject(this.projectId);
                });
            }
        },
        showValidationMessage () {
            if (!this.project.name) {
                this.errorMessage = 'Project must be named.';
            } else if (!this.conversationTopic.topicId) {
                this.errorMessage = 'Project must have a Conversation Topic.';
            } else if (this.projectColumns.length < 1) {
                this.errorMessage = 'Project must have one or more Brand Columns';
            }
        },
        showConfirmArchiveModal (projectToArchive) {
            if (this.warnIfHasChanges()) return;

            this.$buefy.dialog.confirm({
                title: 'Archive Project',
                message: `Are you sure you want to archive <b>` + projectToArchive.name + `</b>?
                    <hr>
                    <i style="font-size: 0.8em">
                        Archiving a project will immediately halt metric collection.
                        Any metrics that have already been collected will continue to be available.
                        The project will continue to be accessible to the owner and any user that it has been shared with.
                    </i>
                `,
                type: 'is-warning',
                hasIcon: true,
                onConfirm: () => this.archiveProject(projectToArchive)
            });
        },
        showConfirmDeleteModal (projectToDelete) {
            this.$buefy.dialog.confirm({
                title: 'Delete Project',
                message: `This will permanently delete your Draft project <b>${projectToDelete.name}</b>.<br>Do you wish to continue?`,
                type: 'is-warning',
                confirmText: 'Delete',
                cancelText: 'Cancel',
                hasIcon: true,
                onConfirm: () => this.deleteProject(projectToDelete)
            });
        },
        openPublishConfirmationModal (projectColumns, callBack, isHasChangeCheckRequired) {
            if (isHasChangeCheckRequired) {
                let warningMsg = 'There are some unsaved changes. You will need to save them before publishing the project.';
                if (this.warnIfHasChanges(warningMsg)) return;
            }

            let modalConfig = {
                parent: this,
                component: ProjectPublishConfirmation,
                hasModalCard: true,
                props: {
                    projectColumns: projectColumns
                },
                events: {
                    confirmPublish: callBack
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        openConversationTopicModal () {
            let modalConfig = {
                parent: this,
                component: NetBaseTopicSelection,
                hasModalCard: true,
                events: {
                    topicSelected: topic => {
                        this.conversationTopic = topic;
                        if (topic.description) {
                            this.projectDescription = topic.description;
                        }
                    }
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        openConversationThemeModal () {
            let modalConfig = {
                parent: this,
                component: NetBaseThemeSelection,
                hasModalCard: true,
                events: {
                    themeSelected: theme => {
                        this.conversationTheme = theme;
                    }
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        clearConversationTheme () {
            this.conversationTheme = {};
        },
        openBrandColumnModal (updateColumn, isPublished = false) {
            let defaultColours = colourService.getColours;
            let colourIndex = this.projectColumns.length % defaultColours.length;
            let modalConfig = {
                parent: this,
                component: BrandColumnForm,
                hasModalCard: true,
                props: {
                    updateColumnData: updateColumn || undefined,
                    isUpdate: !!updateColumn,
                    isPublished,
                    initialColour: updateColumn ? undefined : defaultColours[colourIndex]
                },
                events: {
                    columnSaved: this.saveColumn,
                    columnChanged: this.changeColumn
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        openWeightAdjustmentModal () {
            let modalConfig = {
                parent: this,
                component: ProjectWeightAdjustment,
                hasModalCard: true,
                props: {
                    inputMetricWeights: this.projectMetricWeights,
                    inputPillarWeights: this.projectPillarWeights
                },
                events: {
                    projectMetricWeightsChanged: metricWeights => { this.projectMetricWeights = metricWeights; },
                    projectPillarWeightsChanged: pillarWeights => { this.projectPillarWeights = pillarWeights; },
                    projectWeightsChanged: (metricWeights, pillarWeights) => {
                        this.projectMetricWeights = metricWeights;
                        this.projectPillarWeights = pillarWeights;
                    }
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        saveColumn (savedColumn) {
            if (savedColumn.id) {
                let columnToUpdate = _.find(this.projectColumns, column => column.id === savedColumn.id);
                Object.assign(columnToUpdate, savedColumn);
            } else {
                const alphabet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
                savedColumn.id = nanoidGenerate(alphabet, 10);
                savedColumn.isNew = true;
                this.projectColumns.push(savedColumn);
            }
        },
        changeColumn (changeColumn) {
            if (changeColumn.id) {
                this.needRepublish = true;
                let columnToUpdate = _.find(this.projectColumns, column => column.id === changeColumn.id);
                Object.assign(columnToUpdate, changeColumn);
            }
        },
        deleteColumn (deletedColumn) {
            this.projectColumns = _.filter(this.projectColumns, col => col.id !== deletedColumn.id);
        },
        archiveProject (projectToArchive) {
            projectService.archiveProject(projectToArchive.id)
                .then(response => {
                    this.clearErrorMessage();
                    projectToArchive.state = constants.PROJECT_STATE.ARCHIVED;
                    this.projectEndDate = new Date(response.data.endDate);
                    this.originalContent = this.serializedContent();
                    this.$buefy.toast.open({
                        message: projectToArchive.name + ' has been archived',
                        type: 'is-success'
                    });
                })
                .catch(error => this.$buefy.toast.open({
                    message: error.message,
                    type: 'is-danger'
                }));
        },
        deleteProject (projectToDelete) {
            if (!projectToDelete.id) {
                this.originalContent = this.serializedContent();
                this.$router.push('/');
                return;
            }
            projectService.deleteProject(projectToDelete.id)
                .then(response => {
                    this.clearErrorMessage();
                    this.originalContent = this.serializedContent();
                    this.$router.push('/');
                    this.$buefy.toast.open({
                        message: projectToDelete.name + ' has been deleted',
                        type: 'is-success'
                    });
                })
                .catch(error => this.$buefy.toast.open({
                    message: error.message,
                    type: 'is-danger'
                }));
        },
        serializedContent () {
            const content = _.cloneDeep(this.project);
            content.projectJSON = this.buildProjectJSON();
            return _.pick(content, ['id', 'name', 'state', 'isSharedWithEveryone', 'projectJSON']);
        },
        hasChanges () {
            const currentContent = this.serializedContent();
            return !_.isEqual(this.originalContent, currentContent);
        },
        confirmLoseChange () {
            if (this.hasChanges()) {
                return window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
            }
            return true;
        },
        warnIfHasChanges (message) {
            if (this.hasChanges()) {
                this.$buefy.toast.open({
                    message: message || 'Unsaved changes detected. Please save the project.',
                    type: 'is-danger',
                    duration: 5000
                });
                return true;
            }
            return false;
        },
        warnOnWindowChange (event) {
            if (this.hasChanges()) {
                event.preventDefault();
                event.returnValue = true;
            }
        },
        buildProjectJSON () {
            return JSON.stringify({
                __version: constants.CURRENT_PROJECT_VERSION,
                description: this.projectDescription,
                conversationTopic: {
                    id: this.conversationTopic.topicId,
                    name: this.conversationTopic.name,
                    isAvailable: this.conversationTopic.isAvailable
                },
                conversationTheme: this.conversationTheme && this.conversationTheme.id > 0
                    ? { id: this.conversationTheme.themeId, name: this.conversationTheme.name }
                    : undefined,
                columns: this.projectColumns.map(col => {
                    // exclude properties required only for frontend
                    return _.omit(col, 'isNew');
                }),
                metricWeights: this.hasDefaultMetricWeights ? undefined : this.projectMetricWeights,
                pillarWeights: this.hasDefaultPillarWeights ? undefined : this.projectPillarWeights
            });
        },
        confirmShareUnshareWithEveryone (isSharedWithEveryone, callback) {
            let title = (this.project.isSharedWithEveryone ? 'Share' : 'Unshare') + ' Project';
            let message = this.project.isSharedWithEveryone
                ? `You have selected to share this project with everyone.
                This will make the project available to all the users except client-users.
                <hr />
                Would you like to continue?`
                : `You have selected to unshare this project with everyone.
                This will make the project unavailable to all users except the project owner and any users
                that it has been specifically shared with.
                <hr />
                Would you like to continue?`;
            this.$buefy.dialog.confirm({
                title: title,
                message: message,
                confirmText: 'Yes',
                cancelText: 'No',
                type: 'is-warning',
                hasIcon: true,
                onCancel: () => {
                    this.project.isSharedWithEveryone = !this.project.isSharedWithEveryone;
                }
            });
        },
        openProjectShareModal () {
            let modalConfig = {
                parent: this,
                component: ProjectShare,
                hasModalCard: true,
                props: {
                    project: this.project
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        openProjectCopyModal () {
            let warningMsg = 'There are some unsaved changes. You will need to save them before copying the project.';
            if (this.warnIfHasChanges(warningMsg)) return;

            let modalConfig = {
                parent: this,
                component: ProjectCopyForm,
                hasModalCard: true,
                props: {
                    project: this.project
                },
                events: {
                    copyCompleted: (projectId) => {
                        this.$router.push('/project/edit/' + projectId);
                        this.$buefy.toast.open({
                            message: 'Successfully copied the project.',
                            type: 'is-success'
                        });
                    }
                }
            };
            this.$buefy.modal.open(modalConfig);
        },
        openProjectTransferModal () {
            let warningMsg = 'There are some unsaved changes. You will need to save them before transferring the project.';
            if (this.warnIfHasChanges(warningMsg)) return;

            let ownedProject = Object.assign({}, this.project);
            ownedProject.ownerEmail = localStorage.getItem('email') || '';
            ownedProject.isOwnerActive = true;

            let modalConfig = {
                parent: this,
                component: ProjectTransferForm,
                hasModalCard: true,
                props: {
                    project: ownedProject
                },
                events: {
                    submitEvent: () => this.$router.push('/')
                }
            };

            this.$buefy.modal.open(modalConfig);
        },
        clearErrorMessage () {
            this.errorMessage = '';
        },
        isSocialtoolsPageNotActive (pageInfo) {
            return pageInfo && !pageInfo.isActive;
        },
        isSocialtoolsPageHasName (pageInfo) {
            return pageInfo && pageInfo.name;
        },
        isProjectOwner (project) {
            let userId = localStorage.getItem('userId');
            let projectUserId = project.userId ? project.userId.toString() : '';
            return userId === projectUserId;
        },
        forceRedirectToProjectListView () {
            // Set the flag to force redirect without checking for any changes
            this.forceRedirect = true;
            // Redirect to project list view.
            this.$router.push('/project');
        }
    },
    computed: {
        isValidForSave () {
            return this.project.name && this.projectColumns.length > 0 && this.conversationTopic.topicId;
        },
        isDraft () {
            return this.project.state === constants.PROJECT_STATE.DRAFT;
        },
        isPublished () {
            return this.project.state === constants.PROJECT_STATE.PUBLISHED;
        },
        isPublishing () {
            return this.project.state === constants.PROJECT_STATE.PUBLISHING;
        },
        isArchived () {
            return this.project.state === constants.PROJECT_STATE.ARCHIVED;
        },
        hasNewColumns () {
            return !!_.find(this.projectColumns, col => col.isNew);
        },
        hasDefaultWeights () {
            return this.hasDefaultMetricWeights && this.hasDefaultPillarWeights;
        },
        hasDefaultMetricWeights () {
            return this.projectMetricWeights.length === 0 ||
                JSON.stringify(this.projectMetricWeights) === JSON.stringify(this.defaultMetricWeights);
        },
        hasDefaultPillarWeights () {
            return this.projectPillarWeights.length === 0 ||
                JSON.stringify(this.projectPillarWeights) === JSON.stringify(this.defaultPillarWeights);
        },
        projectColumnHeaders () {
            // check whether there are project brand columns present and if not use the placeholder columns
            return this.projectColumns.length > 0 ? [] : this.placeholderColumnHeaders;
        }
    },
    beforeRouteLeave (to, from, next) {
        // Check for unsaved changes and confirm whether to proceed without saving
        if (!this.forceRedirect && !this.confirmLoseChange()) {
            next(false);
        } else {
            next();
        }
    },
    beforeDestroy () {
        // Unsubscribe from the 'beforeunload' event correctly
        window.removeEventListener('beforeunload', this.warnOnWindowChange);
    }
};

let setColoursToColumns = (columns) => {
    // if a column doesn't have a colour specified then set it here
    let defaultColours = colourService.getColours;
    columns.forEach((col, index) => { if (!col.colour) col.colour = defaultColours[index]; });
};

let handleProjectChangeWith = async (vueContext, message, fnAsynServiceCall) => {
    if (!vueContext.isValidForSave) {
        vueContext.showValidationMessage();
        return;
    }

    vueContext.isSaving = true;

    try {
        let response = await fnAsynServiceCall();
        let projectToEdit = response.data;
        vueContext.clearErrorMessage();

        // Always reload the project to ensure this component is in the correct mode
        vueContext.project = projectToEdit;
        vueContext.projectEndDate = new Date(vueContext.project.endDate);
        vueContext.originalContent = vueContext.serializedContent();

        vueContext.$buefy.toast.open({
            message: message,
            type: 'is-success'
        });

        // Check whether we are in create mode, and redirect to edit mode correctly
        if (vueContext.$route.path === '/project/create') {
            vueContext.$router.push('/project/edit/' + projectToEdit.id);
            vueContext.isEdit = true;
        }
    } catch (error) {
        let errorResponse = (await error).response;
        if (errorResponse && errorResponse.data && errorResponse.data.message) {
            vueContext.errorMessage = errorResponse.data.message;
        } else {
            vueContext.errorMessage = error.message;
        }
    } finally {
        vueContext.isSaving = false;
    }
};

</script>

<style lang="scss" scoped>
section.section.project-details {
    padding-top: 0rem;
    padding-bottom: 2rem;
}

.commands {
    button {
        margin-right: 7px;
    }
}

.project-details {
    .field {
        margin-top:1.5rem;
    }

    .modal-button-control{
        display: flex;
        justify-content:flex-start;
        align-items: center;

        span{
            margin:0 1rem;
        }
    }

    .info-text {
        padding: 0;
        font-size: 0.7em;
        font-style: italic;
        height: 1.2rem;
    }
}

.is-baseline-aligned{
    align-items: baseline;
}
.is-center-aligned{
    align-items: center;
}
</style>

<!--Non-Scoped style-->
<style lang="scss">
.dialog {
    .modal-card {
        max-width: 660px;
    }
}
</style>
